import { useMemo, useState } from 'react';
import {
  Button,
  Input,
  Modal,
  Select,
  Textarea,
  type SelectOption,
  useToast,
} from '../../ui';
import { useRooms } from '../../features/rooms/useRooms';
import { useTemplates } from '../../features/templates/useTemplates';
import { blankRoom, validateRoom } from '../../features/rooms/roomModel';
import type { Room } from '../../features/rooms/types';

export interface CreateRoomModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with the freshly-created room after a successful save. */
  onCreated?: (room: Room) => void;
}

/**
 * Create-room modal. Collects name, description and an optional default
 * template (picked from the Templates feature). The creator + initial owner
 * member are seeded from the Midway identity by the store.
 */
export function CreateRoomModal({
  open,
  onClose,
  onCreated,
}: CreateRoomModalProps) {
  const { toast } = useToast();
  const { create } = useRooms();
  const { groups, loading: templatesLoading } = useTemplates();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [defaultTemplateId, setDefaultTemplateId] = useState('');
  const [saving, setSaving] = useState(false);

  const templateOptions = useMemo<SelectOption[]>(
    () =>
      groups.map((g) => ({
        value: g.latest.id,
        label: `${g.name} (v${g.latest.version})`,
      })),
    [groups],
  );

  const reset = () => {
    setName('');
    setDescription('');
    setDefaultTemplateId('');
  };

  const close = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const onSubmit = async () => {
    // Validate a would-be room using the shared validator.
    const candidate: Room = {
      ...blankRoom(),
      name,
      description,
      defaultTemplateId: defaultTemplateId || undefined,
    };
    const problems = validateRoom(candidate);
    if (problems.length > 0) {
      toast({ tone: 'error', title: 'Cannot create room', description: problems[0] });
      return;
    }
    setSaving(true);
    try {
      const room = await create({
        name,
        description,
        defaultTemplateId: defaultTemplateId || undefined,
      });
      toast({ tone: 'success', title: 'Room created', description: room.name });
      reset();
      onCreated?.(room);
      onClose();
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Create failed',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Create room"
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} onClick={onSubmit}>
            Create room
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Name"
          value={name}
          placeholder="Payments Platform Q3"
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
        <Textarea
          label="Description"
          value={description}
          placeholder="What work does this room track?"
          onChange={(e) => setDescription(e.target.value)}
        />
        <Select
          label="Default template (optional)"
          placeholder={
            templatesLoading ? 'Loading templates…' : 'No default template'
          }
          value={defaultTemplateId}
          options={templateOptions}
          onChange={(e) => setDefaultTemplateId(e.target.value)}
        />
        <p className="text-xs text-zinc-500">
          You'll be added as the room owner. Members and the default template can
          be changed later in room settings.
        </p>
      </div>
    </Modal>
  );
}

export default CreateRoomModal;
